<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import * as am5percent from '@amcharts/amcharts5/percent'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'

const props = defineProps<{
  config: {
    data: { category: string; value: number }[]
    categoryLabel: string
    valueLabel: string
    palette?: number[]
  }
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
let am5Root: am5.Root | null = null

const renderChart = () => {
  if (chartContainer.value) {
    if (am5Root) {
      am5Root.dispose()
    }
    am5Root = am5.Root.new(chartContainer.value)
    am5Root.setThemes([am5themes_Animated.new(am5Root)])

    const chart = am5Root.container.children.push(
      am5percent.PieChart.new(am5Root, {
        layout: am5Root.verticalLayout,
        innerRadius: am5.percent(50),
      }),
    )

    const series = chart.series.push(
      am5percent.PieSeries.new(am5Root, {
        // Map directly to the data keys ("value" and "category"),
        // regardless of what the human-readable label is.
        valueField: 'value',
        categoryField: 'category',
        alignLabels: false,
        legendValueText: "{valuePercentTotal.formatNumber('0.00')}%",
      }),
    )

    // Apply the backend color palette if it exists
    if (props.config.palette && props.config.palette.length > 0) {
      // Backend sends integers (e.g., 27556), am5.color accepts them directly
      series.get('colors')?.set(
        'colors',
        props.config.palette.map((c) => am5.color(c)),
      )
    }

    series.labels.template.set('forceHidden', true)
    series.ticks.template.set('forceHidden', true)

    // Prendiamo i dati dalla config
    series.data.setAll(props.config.data)

    // Aggiungiamo una legenda
    const legend = chart.children.push(
      am5.Legend.new(am5Root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 15,
        marginBottom: 15,
      }),
    )
    legend.data.setAll(series.dataItems)

    series.appear(1000, 100)
  }
}

onMounted(renderChart)
watch(() => props.config, renderChart, { deep: true })
onBeforeUnmount(() => am5Root?.dispose())
</script>

<template>
  <div ref="chartContainer" class="w-full h-[400px]"></div>
</template>
